import { Button, Spin, Select, Switch } from "antd";
import LabeledInput from "components/Form/LabeledInput";
import LabeledTimeInput from "components/Form/LabeledTimeInput";
import useSetting from "hooks/useSetting";
import { CloseCircleOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { FaCog } from "react-icons/fa";
import { Controller } from "react-hook-form";

export default function Settings() {
  const {
    control,
    handleSubmit,
    handleAddSpecialHour,
    handleRemoveSpecialHour,
    loading,
    onSubmit,
    watchSpecialHour,
    data,
    errors,
  } = useSetting();
  return (
    <div
      className="w-full bg-white rounded-lg p-6"
      style={{ boxShadow: "0 4px 25px 0 rgba(0,0,0,.1)" }}
    >
      <div className="w-full pt-5 pb-5">
        <div className="w-full">
          <h1 className="p-5 font-light text-lg text-center">Settings</h1>
          <div className="w-full">
            <div className="w-full relative h-1 top-7 overflow-clip rounded-md">
              <div className="w-1/2 bg-black h-full rounded-md"></div>
            </div>
            <div className="relative w-full flex justify-center">
              <div className="relative h-14 w-14 bg-black rounded-full flex items-center justify-center text-white text-2xl">
                <FaCog className="text-2xl text-white" />
              </div>
            </div>
          </div>
          <p className="text-center mt-2 text-sm font-normal">
            System Settings
          </p>
        </div>
        {data.loading ? (
          <div className="w-full flex items-center justify-center py-20">
            <Spin />
          </div>
        ) : (
          <div className="w-full py-8 px-5">
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="w-full flex flex-wrap"
            >
              {/* Driver Search Radius Settings */}
              <div className="w-full px-4 pt-5">
                <div className="mb-3">
                  <h3 className="text-xl font-bold mb-1 mt-7">
                    Driver Search Radius Settings
                  </h3>
                  <p className="text-sm text-gray-600 mb-2">
                    Configure the geographic area for driver search during trip dispatch.
                    The center point (latitude/longitude) defines where the search starts,
                    and the radius determines how far from the center point drivers will be searched.
                  </p>
                  <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-md mb-4">
                    <p className="font-semibold mb-1">Recommended Radius Values:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Small city/area: 5,000 - 10,000 meters (5-10 km)</li>
                      <li>Medium city: 10,000 - 20,000 meters (10-20 km)</li>
                      <li>Large city: 20,000 - 50,000 meters (20-50 km)</li>
                      <li>Default: 20,000 meters (20 km)</li>
                    </ul>
                    <p className="mt-2 font-semibold">Note: Radius is in meters, not kilometers.</p>
                  </div>
                </div>
                <div className="flex flex-wrap">
                  <div className="w-full md:w-1/3 pr-4">
                    <LabeledInput
                      label="Center Latitude"
                      type="number"
                      step="any"
                      control={control}
                      name="search_range.latitude"
                      placeholder="13.496494229233356"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Center point latitude for driver search
                    </p>
                  </div>
                  <div className="w-full md:w-1/3 pr-4">
                    <LabeledInput
                      label="Center Longitude"
                      type="number"
                      step="any"
                      control={control}
                      name="search_range.longitude"
                      placeholder="39.47494624044569"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Center point longitude for driver search
                    </p>
                  </div>
                  <div className="w-full md:w-1/3 pr-4">
                    <LabeledInput
                      label="Search Radius (meters)"
                      type="number"
                      step="1000"
                      control={control}
                      name="search_range.radius"
                      placeholder="20000"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Search radius in meters (e.g., 20000 = 20 km)
                    </p>
                  </div>
                </div>
              </div>

              {/* Dispatch Expansion Multiplier Settings */}
              <div className="w-full px-4 pt-5">
                <div className="mb-3">
                  <h3 className="text-xl font-bold mb-1 mt-7">
                    Dispatch Expansion Multiplier Settings
                  </h3>
                  <p className="text-sm text-gray-600 mb-2">
                    Controls how the driver search algorithm expands the search radius when looking for available drivers.
                    The system starts with the base search radius and expands it (2x, 3x, etc.) up to the maximum multiplier if no drivers are found.
                  </p>
                  <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-md mb-4">
                    <p className="font-semibold mb-1">How It Works:</p>
                    <ul className="list-disc list-inside space-y-1 mb-2">
                      <li>First search: 1x radius (e.g., 20km)</li>
                      <li>If no drivers: 2x radius (e.g., 40km)</li>
                      <li>If no drivers: 3x radius (e.g., 60km)</li>
                      <li>Continues up to maximum multiplier</li>
                    </ul>
                    <p className="font-semibold mb-1 mt-2">Recommended Values:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Small city: Multiplier 2.0, Timeout 3.0 minutes</li>
                      <li>Medium city: Multiplier 3.0, Timeout 5.0 minutes (default)</li>
                      <li>Large city: Multiplier 5.0, Timeout 7.0 minutes</li>
                    </ul>
                  </div>
                </div>
                <div className="flex flex-wrap">
                  <div className="w-full md:w-1/3 pr-4">
                    <LabeledInput
                      label="Max Expansion Multiplier"
                      type="number"
                      step="0.1"
                      control={control}
                      name="dispatch_config.max_expansion_multiplier"
                      placeholder="3.0"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Maximum number of times to expand search radius (e.g., 3.0 = searches at 1x, 2x, 3x)
                    </p>
                  </div>
                  <div className="w-full md:w-1/3 pr-4">
                    <LabeledInput
                      label="Timeout (minutes)"
                      type="number"
                      step="0.1"
                      control={control}
                      name="dispatch_config.timeout_minutes"
                      placeholder="5.0"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Maximum time (in minutes) to spend searching for drivers
                    </p>
                  </div>
                  <div className="w-full md:w-1/3 pr-4">
                    <LabeledInput
                      label="Dispatch Cycle Count"
                      type="number"
                      step="1"
                      control={control}
                      name="dispatch_config.cycle_count"
                      placeholder="3"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Number of dispatch cycles to attempt before giving up
                    </p>
                  </div>
                </div>
              </div>

              {/* Timing Settings */}
              <div className="w-full px-4 pt-5">
                <h3 className="text-xl font-bold mb-1 mt-7">Timing Settings</h3>
                <div className="flex flex-wrap">
                  <div className="w-full md:w-1/3 pr-4">
                    <LabeledInput
                      label="Waiting Time (in Seconds)"
                      type="number"
                      control={control}
                      name="waiting_time"
                      placeholder="120"
                    />
                  </div>
                </div>
              </div>

              {/* Support Channels */}
              <div className="w-full px-4 pt-5">
                <h3 className="text-xl font-bold mb-1 mt-7">
                  Support Channels
                </h3>
                <div className="flex flex-wrap">
                  <div className="w-full md:w-1/3 pr-4">
                    <LabeledInput
                      label="Call Center"
                      type="tel"
                      control={control}
                      name="call_center"
                      placeholder="call center"
                    />
                  </div>
                  <div className="w-full md:w-1/3 pr-4">
                    <LabeledInput
                      label="Whatsapp Support"
                      type="string"
                      control={control}
                      name="whatsapp_support_center"
                      placeholder="whatsapp support"
                    />
                  </div>
                  <div className="w-full md:w-1/3 pr-4">
                    <LabeledInput
                      label="Telegram Support"
                      type="text"
                      control={control}
                      name="telegram_support_center"
                      placeholder="telegram support"
                    />
                  </div>
                  <div className="w-full md:w-1/3 pr-4 pt-4">
                    <LabeledInput
                      label="Email Support"
                      type="email"
                      control={control}
                      name="email_support_center"
                      placeholder="email support"
                    />
                  </div>
                </div>
              </div>

              {/* Financial Settings */}
              <div className="w-full px-4 pt-5">
                <h3 className="text-xl font-bold mb-1 mt-7">
                  Financial Settings
                </h3>
                <div className="flex flex-wrap">
                  <div className="w-full md:w-1/3 pr-4">
                    <LabeledInput
                      label="Tax Percentage (in %)"
                      type="number"
                      control={control}
                      name="tax_percent"
                      placeholder="15"
                    />
                  </div>
                  <div className="w-full md:w-1/3 pr-4">
                    <LabeledInput
                      label="Cut Percentage (in %)"
                      type="number"
                      control={control}
                      name="cut_percent"
                      placeholder="cut percentage"
                    />
                  </div>
                  <div className="w-full md:w-1/3 pr-4 pt-4">
                    <LabeledInput
                      label="Driver Min Wallet"
                      type="number"
                      control={control}
                      name="driver_min_wallet"
                      placeholder="100"
                    />
                  </div>
                  <div className="w-full md:w-1/3 pr-4 pt-4">
                    <LabeledInput
                      label="High Demand Percent (in %)"
                      type="number"
                      control={control}
                      name="high_demand_percent"
                      placeholder="4%"
                    />
                  </div>
                  <div className="w-full md:w-1/3 pr-4 pt-4">
                    <LabeledInput
                      label="cancellation Percent (in %)"
                      type="number"
                      control={control}
                      name="cancellation_percent"
                      placeholder="10"
                    />
                  </div>
                  <div className="w-full md:w-1/3 pr-4 pt-4">
                    <LabeledInput
                      label="Max Driver Debt Limit"
                      type="number"
                      control={control}
                      name="driver_max_debt"
                      placeholder="50"
                    />
                    <p className="text-xs text-gray-400 mt-1 italic">Allows drivers to reach negative balance up to this amount.</p>
                  </div>
                </div>
              </div>

              {/* Cancellation Fee Settings */}
              <div className="w-full px-4 pt-5">
                <h3 className="text-xl font-bold mb-1 mt-7">
                  Cancellation Fee Settings
                </h3>
                <div className="flex flex-wrap">
                  <div className="w-full md:w-1/3 pr-4 pt-4">
                    <LabeledInput
                      label="Cancellation Amount"
                      type="number"
                      control={control}
                      name="cancellation_fee.amount"
                      placeholder="10"
                    />
                  </div>
                  <div className="w-full md:w-1/3 pr-4 pt-4">
                    <LabeledInput
                      label="Cancellation Count "
                      type="number"
                      control={control}
                      name="cancellation_fee.count"
                      placeholder="3"
                    />
                  </div>
                </div>
              </div>

              {/* Driver Decline Penalty System */}
              <div className="w-full px-4 pt-5">
                <h3 className="text-xl font-bold mb-1 mt-7">
                  Driver Decline Penalty System
                </h3>
                <div className="flex flex-wrap">
                  <div className="w-full md:w-1/3 pr-4 pt-4">
                    <LabeledInput
                      label="Max Daily Declines"
                      type="number"
                      control={control}
                      name="decline_penalty_count"
                      placeholder="3"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Max allowed declines per day before penalty
                    </p>
                  </div>
                  <div className="w-full md:w-1/3 pr-4 pt-4">
                    <LabeledInput
                      label="Decline Penalty Amount"
                      type="number"
                      control={control}
                      name="decline_penalty_amount"
                      placeholder="50"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Amount to deduct per excess decline
                    </p>
                  </div>
                  <div className="w-full md:w-1/3 pr-4 pt-4">
                    <LabeledInput
                      label="Penalty Expiry (hours)"
                      type="number"
                      control={control}
                      name="penalty_expiry_hours"
                      placeholder="24"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Hours before penalty automatically expires
                    </p>
                  </div>
                </div>
              </div>

              {/* Automated Suspension & Ban System */}
              <div className="w-full px-4 pt-5">
                <h3 className="text-xl font-bold mb-1 mt-7 text-red-600">
                  Automated Suspension & Ban System
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Define triggers for automatic account suspension. This system monitors operational strikes (like excessive declines) 
                  and automatically blocks drivers for a cooldown period.
                </p>
                <div className="flex flex-wrap items-center">
                  <div className="w-full md:w-1/3 pr-4 pt-4 flex flex-col">
                    <label className="text-sm font-medium mb-3">Auto-Ban System Enabled</label>
                    <Controller
                      name="auto_ban_on_penalty_enabled"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                          checkedChildren="ON"
                          unCheckedChildren="OFF"
                          className={field.value ? "bg-red-500" : ""}
                        />
                      )}
                    />
                    <p className="text-xs text-gray-500 mt-2">
                       When ON, drivers reaching maximum strikes will be banned automatically.
                    </p>
                  </div>
                  <div className="w-full md:w-1/3 pr-4 pt-4">
                    <LabeledInput
                      label="Strikes Needed for Ban"
                      type="number"
                      control={control}
                      name="max_penalty_strikes_for_ban"
                      placeholder="3"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Number of penalties (declines/idleness) to trigger an auto-ban.
                    </p>
                  </div>
                  <div className="w-full md:w-1/3 pr-4 pt-4">
                    <LabeledInput
                      label="Suspension Duration (hours)"
                      type="number"
                      control={control}
                      name="auto_unban_duration_hours"
                      placeholder="24"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      How long the account remains locked before auto-unbanning.
                    </p>
                  </div>
                </div>
              </div>

              {/* Audit & Anti-Idle System */}
              <div className="w-full px-4 pt-5">
                <h3 className="text-xl font-bold mb-1 mt-7">
                  Audit & Anti-Idle System
                </h3>
                <div className="flex flex-wrap">
                  <div className="w-full md:w-1/3 pr-4 pt-4">
                    <LabeledInput
                      label="Min Daily Trips"
                      type="number"
                      control={control}
                      name="min_daily_trips"
                      placeholder="1"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Minimum trips a driver must start per day to avoid penalty
                    </p>
                  </div>
                  <div className="w-full md:w-1/3 pr-4 pt-4">
                    <LabeledInput
                      label="Idle Penalty Amount"
                      type="number"
                      control={control}
                      name="idle_penalty_amount"
                      placeholder="20"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Deduction if min trips not met
                    </p>
                  </div>
                </div>
              </div>

              {/* Trip Lottery System Settings */}
              <div className="w-full px-4 pt-5">
                <h3 className="text-xl font-bold mb-1 mt-7">
                  Trip Lottery System Settings
                </h3>
                <div className="flex flex-wrap">
                  <div className="w-full md:w-1/3 pr-4 pt-4 flex flex-col justify-center">
                    <label className="text-sm font-medium mb-3">Lottery Enabled</label>
                    <Controller
                      name="lottery_enabled"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                          checkedChildren="ON"
                          unCheckedChildren="OFF"
                        />
                      )}
                    />
                  </div>
                  <div className="w-full md:w-1/3 pr-4 pt-4">
                    <label className="text-sm font-medium mb-1 block">Rollout Days</label>
                    <Controller
                      name="lottery_rollout_days"
                      control={control}
                      render={({ field }) => (
                        <Select
                          mode="multiple"
                          style={{ width: '100%' }}
                          placeholder="Select rollout days"
                          value={field.value}
                          onChange={field.onChange}
                          options={[
                            { label: 'Monday', value: 'monday' },
                            { label: 'Tuesday', value: 'tuesday' },
                            { label: 'Wednesday', value: 'wednesday' },
                            { label: 'Thursday', value: 'thursday' },
                            { label: 'Friday', value: 'friday' },
                            { label: 'Saturday', value: 'saturday' },
                            { label: 'Sunday', value: 'sunday' },
                          ]}
                        />
                      )}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Days when winners are automatically selected
                    </p>
                  </div>
                  <div className="w-full md:w-1/3 pr-4 pt-4">
                    <LabeledInput
                      label="Winners per Rollout"
                      type="number"
                      control={control}
                      name="lottery_winners_per_rollout"
                      placeholder="10"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Number of winners selected per rollout cycle
                    </p>
                  </div>
                  <div className="w-full md:w-1/3 pr-4 pt-4">
                    <LabeledInput
                      label="Lottery Grand Prize"
                      type="text"
                      control={control}
                      name="lottery_prize"
                      placeholder="e.g. 5,000 ETB or Brand New Smartphone"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      This is what winners will be awarded (can be text or numbers)
                    </p>
                  </div>
                  <div className="w-full md:w-1/3 pr-4 pt-4">
                    <label className="text-sm font-medium mb-1 block">Lottery Mode (Ticket Type)</label>
                    <Controller
                      name="lottery_split_enabled"
                      control={control}
                      render={({ field }) => (
                        <Select
                          style={{ width: '100%' }}
                          value={field.value}
                          onChange={field.onChange}
                          options={[
                            { label: 'Separate Tickets (Passenger & Driver)', value: true },
                            { label: 'Shared Ticket (One per Trip)', value: false },
                          ]}
                        />
                      )}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Choose if Passenger and Driver win separately or share one ticket
                    </p>
                  </div>
                </div>
              </div>


              {/* Special Hours Setting */}
              <div className="w-full px-4 pt-5" id="working-time">
                <div className="w-full flex justify-between items-center">
                  <h3 className="text-xl font-bold mb-1 mt-7">
                    Special Hours Setting
                  </h3>
                  <Button
                    onClick={handleAddSpecialHour}
                    type="primary"
                    shape="circle"
                    className="shadow-none h-9 w-9 p-0 pt-[0.1] flex items-center justify-center font-bold text-2xl"
                  >
                    <PlusCircleOutlined />
                  </Button>
                </div>
                <span className="text-xs text-red-500 font-bold">
                  {errors.special_hour?.message ??
                    errors.special_hour?.root?.message}
                </span>
                <div className="flex flex-col gap-y-5 pt-2">
                  {watchSpecialHour.map((_, key) => (
                    <div
                      className="flex flex-col gap-y-2 border border-gray-300 rounded-md p-4 relative"
                      key={key}
                    >
                      <Button
                        onClick={() => handleRemoveSpecialHour(key)}
                        type="link"
                        danger
                        shape="circle"
                        className="absolute -top-3.5 -right-3.5 shadow-none h-9 w-9 p-0 pt-[0.1] flex items-center justify-center font-bold text-2xl"
                      >
                        <CloseCircleOutlined />
                      </Button>
                      <LabeledInput
                        label="Working Time Name"
                        control={control}
                        name={`special_hour.${key}.name`}
                        placeholder="Morning Working Time"
                        type="text"
                      />
                      <div className="flex flex-wrap">
                        <div className="w-full md:w-1/3 pr-4">
                          <LabeledTimeInput
                            label="Working Time Start"
                            control={control}
                            name={`special_hour.${key}.start_at`}
                            placeholder="02:00:00"
                          />
                        </div>
                        <div className="w-full md:w-1/3 pr-4">
                          <LabeledTimeInput
                            label="Working Time End"
                            control={control}
                            name={`special_hour.${key}.end_at`}
                            placeholder="04:00:00"
                          />
                        </div>
                        <div className="w-full md:w-1/3 pr-4">
                          <LabeledInput
                            label="Percent (in %)"
                            control={control}
                            type="number"
                            name={`special_hour.${key}.percent`}
                            placeholder="1"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* App & Integration Settings */}
              <div className="w-full px-4 pt-5">
                <h3 className="text-xl font-bold mb-1 mt-7">
                  App & Integration Settings
                </h3>
                <div className="flex flex-wrap">
                  <div className="w-full md:w-1/2 pr-4">
                    <LabeledInput
                      label="Map API Key"
                      type="text"
                      control={control}
                      name="map_api_key"
                      placeholder=""
                    />
                  </div>
                  <div className="w-full md:w-1/2 pr-4">
                    <LabeledInput
                      label="App Store Link"
                      type="text"
                      control={control}
                      name="app_store_url"
                      placeholder="-"
                    />
                  </div>
                  <div className="w-full md:w-1/2 pr-4 pt-4">
                    <LabeledInput
                      label="Play Store Link"
                      type="text"
                      control={control}
                      name="playstore_url"
                      placeholder=""
                    />
                  </div>
                </div>
              </div>

              {/* Mileage Settings */}
              <div className="w-full px-4 pt-5">
                <h3 className="text-xl font-bold mb-1 mt-7">
                  Mileage Settings
                </h3>
                <div className="flex flex-wrap">
                  <div className="w-full md:w-1/3 pr-4">
                    <LabeledInput
                      label="User Register Mile Amount"
                      type="text"
                      control={control}
                      name="welcome_mile"
                      placeholder=""
                    />
                  </div>
                  <div className="w-full md:w-1/3 pr-4">
                    <LabeledInput
                      label="Trip Mile Amount"
                      type="text"
                      control={control}
                      name="trip_mile"
                      placeholder=""
                    />
                  </div>
                </div>
              </div>

              {/* Color Settings */}
              <div className="w-full px-4 pt-5">
                <h3 className="text-xl font-bold mb-1 mt-7">
                  Color Settings (in hex)
                </h3>
                <div className="flex flex-wrap">
                  <div className="w-full md:w-1/4 pr-4">
                    <LabeledInput
                      label="Low Intensity"
                      type="text"
                      control={control}
                      name="low_intense_color"
                      placeholder="80FFDDEE"
                    />
                  </div>
                  <div className="w-full md:w-1/4 pr-4">
                    <LabeledInput
                      label="Normal Intensity"
                      type="text"
                      control={control}
                      name="normal_intense_color"
                      placeholder="80FFDDEE"
                    />
                  </div>
                  <div className="w-full md:w-1/4 pr-4">
                    <LabeledInput
                      label="Medium Intensity"
                      type="text"
                      control={control}
                      name="medium_intense_color"
                      placeholder="80FFDDEE"
                    />
                  </div>
                  <div className="w-full md:w-1/4 pr-4">
                    <LabeledInput
                      label="High Intensity"
                      type="text"
                      control={control}
                      name="high_intense_color"
                      placeholder="80FFDDEE"
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="w-full flex mt-8 justify-end px-4">
                <Button
                  loading={loading}
                  htmlType="submit"
                  type="primary"
                  className="py-2 px-8 rounded-md float-right h-auto font-medium w-fit shadow-none"
                >
                  Save Settings
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
